import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { apiUc } from '../http';

/** @typedef {'limited-by-queue' | 'queue-sum' | 'limited-by-agent'} TypesOfLimitation */

const limitationOptions = [
  {
    label: 'Somatória de todas as filas',
    value: 'queue-sum'
  },
  {
    label: 'Vagas limitadas por fila',
    value: 'limited-by-queue'
  },
  {
    label: 'Quantidade de vagas limitadas por atendente',
    value: 'limited-by-agent'
  }
];

const GeneralQueueConfigSchema = yup.object({
  typeOfLimitation: yup
    .string()
    .nullable()
    .defined()
    .oneOf(
      ['queue-sum', 'limited-by-queue', 'limited-by-agent', null],
      'Tipo de limitação inválido'
    ),
  monitoringReloadTime: yup
    .number()
    .transform((value) =>
      Number.isNaN(value) || value === '' || value === null ? 0 : value
    )
    .default(0)
});

export const useGeneralQueueConfig = () => {
  const [config, setConfig] = useState({
    typeOfLimitation: null,
    monitoringReloadTime: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data } = await apiUc.get('/api/config/filas/geral');
      const rawData = data?.data || {};

      const validation = GeneralQueueConfigSchema.safeValidateSync(rawData, {
        stripUnknown: true
      });

      if (!validation.success) {
        console.error('Erro na validação dos dados:', validation.error);
        toast.error('Configurações recebidas com formato inválido');
        return;
      }

      const { data: validatedData } = validation;

      const foundOption = limitationOptions.find(
        (opt) => opt.value === validatedData.typeOfLimitation
      );

      setConfig({
        typeOfLimitation: foundOption || null,
        monitoringReloadTime: validatedData.monitoringReloadTime ?? ''
      });
    } catch (err) {
      console.error(err);
      toast.error('Não foi possível resgatar as configurações gerais', {
        autoClose: 3000,
        closeOnClick: true
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const update = useCallback(async (formData) => {
    setIsLoading(true);
    try {
      const payload = {
        typeOfLimitation: formData.typeOfLimitation?.value,
        monitoringReloadTime: Number(formData.monitoringReloadTime)
      };

      const validationPayload =
        GeneralQueueConfigSchema.safeValidateSync(payload);
      if (!validationPayload.success) {
        console.error(validationPayload.error);
        throw new Error('Payload inválido');
      }

      await apiUc.put('/api/config/filas/geral', payload);

      setConfig({
        typeOfLimitation: formData.typeOfLimitation,
        monitoringReloadTime: formData.monitoringReloadTime
      });

      toast.info('Configurações atualizadas com sucesso!', {
        autoClose: 3000,
        closeOnClick: true
      });
      return true;
    } catch (err) {
      console.error(err);
      toast.error('Não foi possível atualizar as configurações', {
        autoClose: 3000,
        closeOnClick: true
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    limitationOptions,
    config,
    update,
    isLoading
  };
};
